/**
 * `materials.*` wire contract (garment Increment 5, `garment.materials`).
 *
 * The Style File's Materials section + the BOM→vendor-PO / issue-to-cutting
 * actions. `*Milli` quantities are integers ×1000; the buy rate crosses as
 * stringified paise (`moneyPaise`). Behind `entitledProcedureFor("garment.materials")`.
 */
import { z } from "zod";
import { uuid, moneyPaise } from "../_common.js";

/**
 * The single next step for a material line. Replaces the older four-value
 * `materialStatus`, which answered "is there enough?" and left the user to
 * translate that into an action by reading five more numbers.
 */
export const materialPlan = z.enum(["issued", "ready", "partly_ready", "awaiting", "to_source"]);
export const pinView = z.enum(["front", "back"]);
const qtyMilliInt = z.number().int().min(0).max(2_000_000_000);
const pinCoord = z.number().min(0).max(100);

export const getForStyleInput = z.object({ styleId: uuid });

export const materialsView = z.object({
  styleId: uuid,
  hasBom: z.boolean(),
  lines: z.array(
    z.object({
      bomLineId: uuid,
      kind: z.string(),
      label: z.string(),
      itemId: uuid.nullable(),
      uom: z.string(),
      requiredQtyMilli: qtyMilliInt,
      onHandQtyMilli: qtyMilliInt,
      orderedQtyMilli: qtyMilliInt,
      receivedQtyMilli: qtyMilliInt,
      issuedQtyMilli: qtyMilliInt,
      shortByMilli: qtyMilliInt,
      /** Issuable right now — unissued receipts + warehouse stock. */
      availableQtyMilli: qtyMilliInt,
      /** Still to reach cutting (required − issued). */
      stillNeededQtyMilli: qtyMilliInt,
      /** Still to buy, after everything already available. */
      toOrderQtyMilli: qtyMilliInt,
      vendorName: z.string().nullable(),
      poCount: z.number().int().nonnegative(),
      primaryPurchaseOrderId: uuid.nullable(),
      plan: materialPlan,
      pinX: z.number().nullable(),
      pinY: z.number().nullable(),
      pinView: pinView.nullable(),
    }),
  ),
});
export const getForStyleOutput = materialsView;

/** Place a BOM line's hotspot on the Visual Style File sketch (ADR-0029). */
export const placeHotspotInput = z.object({
  bomLineId: uuid,
  pinX: pinCoord,
  pinY: pinCoord,
  pinView,
});
export const placeHotspotOutput = z.object({ id: uuid });

export const convertToPoInput = z.object({
  bomLineId: uuid,
  vendorId: uuid,
  ratePaisePerUnit: moneyPaise,
  taxRatePercent: z.string().regex(/^\d+(\.\d{0,2})?$/).optional(),
  orderQtyMilli: qtyMilliInt.optional(),
  send: z.boolean().optional(),
});
export const convertToPoOutput = z.object({ purchaseOrderId: uuid, materialPoId: uuid });

export const issueToCuttingInput = z.object({
  bomLineId: uuid,
  issuedQtyMilli: z.number().int().positive().max(2_000_000_000),
  pinRef: z.string().trim().max(32).nullable().optional(),
  notes: z.string().trim().max(2000).nullable().optional(),
});
export const issueToCuttingOutput = z.object({ id: uuid });

/** One-click issue of every ready material — see `issue-ready-to-cutting.ts`. */
export const issueReadyToCuttingInput = z.object({ styleId: uuid });
export const issueReadyToCuttingOutput = z.object({
  issued: z.array(
    z.object({
      bomLineId: uuid,
      label: z.string(),
      qtyMilli: z.number().int().nonnegative(),
      uom: z.string(),
    }),
  ),
});

export const issueFromStockInput = z.object({
  bomLineId: uuid,
  warehouseId: uuid,
  qtyMilli: z.number().int().positive().max(2_000_000_000),
  ratePaise: moneyPaise,
  notes: z.string().trim().max(2000).nullable().optional(),
});
export const issueFromStockOutput = z.object({ id: uuid });

/**
 * "I already have this" — record material the factory is holding before any PO,
 * so the rest of Sourcing can see it as real warehouse stock.
 *
 * `warehouseId` is optional: with one warehouse (or none) the server picks it,
 * creating a default store if the company has never set one up.
 */
export const declareInStockInput = z.object({
  bomLineId: uuid,
  qtyMilli: z.number().int().positive().max(2_000_000_000),
  warehouseId: uuid.nullable().optional(),
  ratePaise: moneyPaise.nullable().optional(),
  notes: z.string().trim().max(2000).nullable().optional(),
});
/** The flags report the setup the server had to do, so the UI can say so plainly. */
export const declareInStockOutput = z.object({
  bomLineId: uuid,
  itemId: uuid,
  warehouseId: uuid,
  qtyMilli: z.number().int().nonnegative(),
  createdItem: z.boolean(),
  createdWarehouse: z.boolean(),
  enabledStockTracking: z.boolean(),
});

export const reportShortageInput = z.object({
  bomLineId: uuid,
  note: z.string().trim().max(2000).nullable().optional(),
});
export const reportShortageOutput = z.object({ styleId: uuid });

// Fabric inward worklist (cross-order) — POs awaiting receipt + inspection.
const inwardRow = z.object({
  purchaseOrderId: uuid,
  styleId: uuid,
  styleRef: z.string(),
  styleName: z.string(),
  bomLineId: uuid,
  label: z.string(),
  kind: z.string(),
  itemId: uuid.nullable(),
  vendorName: z.string(),
  orderedMilli: z.number().int(),
  receivedMilli: z.number().int(),
  uom: z.string(),
  receiptStatus: z.enum(["awaiting", "partial", "received"]),
  inspection: z.enum(["na", "none", "pending", "passed", "flagged", "hold"]),
});
export const inwardBoardView = z.object({
  rows: z.array(inwardRow),
  totals: z.object({
    awaiting: z.number().int(),
    partial: z.number().int(),
    needsInspection: z.number().int(),
  }),
});
export const inwardBoardOutput = inwardBoardView;

export type MaterialPlan = z.infer<typeof materialPlan>;
export type MaterialsViewWire = z.infer<typeof materialsView>;
export type InwardBoardViewWire = z.infer<typeof inwardBoardView>;
